import React = require('react');
import IconTextButton from "diy/custom-components/icon-text-button/IconTextButton";
import ColorSelectorView, {ColorType} from "./ColorSelectorView";
import ImageSelectorView from "./ImageSelectorView";
import {BackgroundType, BackgroundSettings, ShadingSetting} from "../../state";
import l10n = require('diy/util/l10n');
import ColorUtil from 'diy/util/color';
import {autoprefixer} from "diy/material-ui/autoprefixer";
import NotInterestedIcon from 'material-ui/svg-icons/av/not-interested';
import PaletteIcon from 'material-ui/svg-icons/image/palette';
import ImageIcon from 'material-ui/svg-icons/image/image';
import {layoutData} from 'diy/util/layoutData';
import FlatButton from 'material-ui/FlatButton';

type SvgIconClass = typeof __MaterialUI.SvgIcon;

export enum Tab {
    None,
    Color,
    Image
};

const bgTypeToTabMap = new Map<BackgroundType, Tab>()
    .set(BackgroundType.None, Tab.None)
    .set(BackgroundType.Color, Tab.Color)
    .set(BackgroundType.Theming, Tab.Color)
    .set(BackgroundType.Image, Tab.Image);

export interface BackgroundSelectorViewProps extends React.Props<BackgroundSelectorView> {
    background: BackgroundSettings;
    parallaxAvailable: boolean;
    onlyColor?: boolean;
    onChange: (props: BackgroundSettings) => void;
    onImageChange: (
        imageId: number,
        imageType: string,
        successCallback: () => void,
        errorCallback: () => void
    ) => void;
    onRequestClose?: () => void;
    onColorClicked?: (backgroundColorType: ColorType, value: string) => void;
    onTabClicked?: (tab: Tab) => void;
    onApplyOnAllGroups?: () => void;
    onEditImageClicked(): void;
    onSelectImageClicked(): void;
}

interface BackgroundSelectorViewState {
    currentTab: Tab;
};

export default class BackgroundSelectorView
extends React.Component<BackgroundSelectorViewProps, BackgroundSelectorViewState> {
    constructor(props: BackgroundSelectorViewProps) {
        super(props);

        this.state = {
            currentTab: bgTypeToTabMap.get(props.background.type)
        } as BackgroundSelectorViewState;
    }

    renderButton(
        type: BackgroundType, label: string, Icon: SvgIconClass, onTouchTap: () => void
    ) {
        let tab = bgTypeToTabMap.get(type);

        return <IconTextButton
            text={label}
            Icon={<Icon/>}
            style={autoprefixer({flexGrow: 1})}
            buttonStyle={{width: "100%"}}
            onTouchTap={() => {onTouchTap();}}
            useAlternativeColors={true}
            isPressed={this.state.currentTab === tab}
            dataTestId={"background-selector-type-" + tab}
        />;
    }
    changeBackground(background: BackgroundSettings) {
        if (this.props.background.type !== background.type) {
            this.props.onChange(background);
        }
    }
    renderDivider() {
        return <div style={{width: 3}} />;
    }
    renderApplyOnAllGroups() {
        if (this.props.onApplyOnAllGroups) {
            return <div style={{
                        padding: 10,
                        textAlign: 'right'
                    }}>
                        <FlatButton
                            label={l10n.getString('section.backgroundSettings.applyToAll')}
                            primary={true}
                            onTouchTap={() => { this.props.onApplyOnAllGroups(); }}
                        />
                    </div>
            ;
        }
        return null;
    }
    render() {
        return (
            <div>
                <div
                    style={{
                        fontFamily: "open sans",
                        fontSize: '14px',
                        lineHeight: '14px',
                        padding: '10px 10px 0 10px',
                        /*put the padding here to could align the custom color box to the popover width */
                        color: '#666'
                    }}
                >{l10n.getString('section.backgroundSettings.title')}</div>
                {!this.props.onlyColor ?
                <div style={autoprefixer({
                    display: 'flex',
                    flexDirection: 'row',
                    padding: 10,
                    /*put the padding here so we could align the custom color box to the popover width, no spacing*/
                })}>
                    {this.renderButton(
                        BackgroundType.None,
                        l10n.getString('section.backgroundSettings.types.none.label'),
                        NotInterestedIcon,
                        () => {
                            this.setState({currentTab: Tab.None});
                            this.changeBackground({
                                type: BackgroundType.None
                            });
                            if (this.props.onTabClicked) {
                                this.props.onTabClicked(Tab.None);
                            }
                        })
                    }
                    {this.renderDivider()}
                    {this.renderButton(
                        BackgroundType.Color,
                        l10n.getString('section.backgroundSettings.types.color.label'),
                        PaletteIcon,
                        () => {
                            this.setState({currentTab: Tab.Color});
                            this.changeBackground(this.getLastColorBackground());
                            if (this.props.onTabClicked) {
                                this.props.onTabClicked(Tab.Color);
                            }
                        }
                    )}
                    {this.renderDivider()}
                    {this.renderButton(
                        BackgroundType.Image,
                        l10n.getString('section.backgroundSettings.types.image.label'),
                        ImageIcon,
                        () => {
                            this.setState({currentTab: Tab.Image});
                            if (this.props.background.imageUrl && this.props.background.imageUrl.length) {
                                this.changeBackground({
                                    type: BackgroundType.Image
                                });
                            }
                            if (this.props.onTabClicked) {
                                this.props.onTabClicked(Tab.Image);
                            }
                        }
                    )}
                </div> :
                    //separate the title from whatever follows
                    <div style={{height: 10}} />
                }
                {
                    this.state.currentTab === Tab.Color || this.props.onlyColor ?
                        <ColorSelectorView
                            type={this.props.background.type}
                            color={this.props.background.color ? this.props.background.color : null}
                            themingZone={
                                this.props.background.zone !== null ?
                                    this.props.background.zone :
                                    null
                            }
                            showThemingZones={layoutData.hasTheming() }
                            onColorSelected={(value, colorType) => {
                                if (this.props.onColorClicked) {
                                    this.props.onColorClicked(colorType, ColorUtil.rgb2hex(value));
                                }

                                this.props.onChange({
                                    type: BackgroundType.Color,
                                    zone: null,
                                    color: value
                                } as BackgroundSettings);
                            }}
                            onThemingZoneSelected={v => {
                                if (this.props.onColorClicked) {
                                    this.props.onColorClicked(ColorType.Theming, v.toString());
                                }

                                this.props.onChange({
                                    type: BackgroundType.Theming,
                                    zone: v,
                                    color: null
                                } as BackgroundSettings);
                            }}
                        /> :
                        (
                            this.state.currentTab === Tab.Image ?
                                <div style={{paddingTop: 10}}>
                                    <ImageSelectorView
                                        imageUrl={this.props.background.imageUrl}
                                        parallaxAvailable={this.props.parallaxAvailable}
                                        parallaxEnabled={this.props.parallaxAvailable ?
                                            this.props.background.parallax : null}
                                        onImageChange={this.props.onImageChange}
                                        onEditImageClicked={this.props.onEditImageClicked}
                                        onSelectImageClicked={this.props.onSelectImageClicked}
                                        shading={this.props.background.shading}
                                        onShadingChange={(shading: ShadingSetting) => {
                                            let newShaddingIsDifferent =
                                                shading.alpha !== this.props.background.shading.alpha ||
                                                shading.color[0] !== this.props.background.shading.color[0] ||
                                                shading.color[1] !== this.props.background.shading.color[1] ||
                                                shading.color[2] !== this.props.background.shading.color[2] ||
                                                shading.enabled !== this.props.background.shading.enabled;

                                            if (newShaddingIsDifferent) {
                                                this.props.onChange({
                                                    type:  BackgroundType.Image,
                                                    shading: shading
                                                });
                                            }
                                        }}
                                        onParallaxToggle={() => {
                                            this.props.onChange({
                                                type: void 0,
                                                parallax: !this.props.background.parallax
                                            } as BackgroundSettings);
                                        }}
                                    />
                                </div> : null
                        )
                }
                {this.state.currentTab !== Tab.Image ? this.renderApplyOnAllGroups() : null}
            </div>
        );
    }

    private getLastColorBackground() {
        let bg = this.props.background;
        let lastColor = bg.color;
        let lastZone = bg.zone;

        // Default checked is the first theming zone
        let type = BackgroundType.Theming;
        let zone = 0;
        let color: typeof lastColor = null;

        if (lastColor !== null) {
            type = BackgroundType.Color;
            zone = null;
            color = lastColor;
        } else {
            if (lastZone !== null) {
                zone = bg.zone;
            }
        }

        return {
            type,
            zone,
            color
        };
    }
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/module/section/components/common/BackgroundSelectorView.tsx
 **/